import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useCurrentRole } from "@/lib/auth/roles";
import { ROUTES } from '@/lib/routes';

interface OnboardingStatus {
  id?: string;
  user_id: string;
  team_id: string;
  completed: boolean | null;
  step_welcome: boolean | null;
  step_players: boolean | null;
  step_data: boolean | null;
  completed_at?: string | null;
}

interface OnboardingContextType {
  showOnboarding: boolean;
  onboardingStatus: OnboardingStatus | null;
  loading: boolean;
  updateStep: (step: keyof OnboardingStatus, value: boolean) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  restartOnboarding: () => Promise<void>;
  checkOnboardingStatus: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType>({
  showOnboarding: false,
  onboardingStatus: null,
  loading: true,
  updateStep: async () => {},
  completeOnboarding: async () => {},
  restartOnboarding: async () => {},
  checkOnboardingStatus: async () => {},
});

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const { role, isLoading: roleLoading } = useCurrentRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (!roleLoading) {
      checkOnboardingStatus();
    }
  }, [roleLoading, role]);

  const checkOnboardingStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      // Players should never see onboarding (player_viewer role)
      if (role === "player_viewer") {
        setShowOnboarding(false);
        setLoading(false);
        return;
      }

      // Get the user's first organization
      let teamId: string | null = null;

      const { data: userOrgs } = await supabase
        .from("user_organization_roles")
        .select("organization_id, organizations(id, teams(id))")
        .eq("user_id", session.user.id)
        .limit(1);

      if (userOrgs && userOrgs.length > 0) {
        const org: any = userOrgs[0];
        if (org.organizations?.teams && org.organizations.teams.length > 0) {
          teamId = org.organizations.teams[0].id;
        }
      }

      // Fallback: check if user owns any teams directly (legacy support)
      if (!teamId) {
        const { data: ownedTeams } = await supabase
          .from("teams")
          .select("id")
          .eq("user_id", session.user.id)
          .limit(1);

        if (ownedTeams && ownedTeams.length > 0) {
          teamId = ownedTeams[0].id;
        }
      }

      if (!teamId) {
        setShowOnboarding(false);
        setLoading(false);
        return;
      }

      // Check onboarding status
      const { data: status, error } = await supabase
        .from("onboarding_status")
        .select("*")
        .eq("user_id", session.user.id)
        .eq("team_id", teamId)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error checking onboarding:", error);
      }

      // If no status exists, create one
      if (!status) {
        const { data: newStatus } = await supabase
          .from("onboarding_status")
          .insert({
            user_id: session.user.id,
            team_id: teamId,
            completed: false,
            step_welcome: false,
            step_players: false,
            step_data: false,
          })
          .select()
          .single();

        setOnboardingStatus(newStatus as OnboardingStatus | null);
        
        setShowOnboarding(true);
      } else {
        setOnboardingStatus(status as OnboardingStatus);
        
        setShowOnboarding(!status.completed);
      }
    } catch (error) {
      console.error("Error in checkOnboardingStatus:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStep = async (step: keyof OnboardingStatus, value: boolean) => {
    if (!onboardingStatus) return;

    try {
      const { data, error } = await supabase
        .from("onboarding_status")
        .update({ [step]: value })
        .eq("id", onboardingStatus.id ?? '')
        .select()
        .single();

      if (error) throw error;
      setOnboardingStatus(data as OnboardingStatus | null);
    } catch (error) {
      console.error("Error updating onboarding step:", error);
    }
  };

  const completeOnboarding = async () => {
    if (!onboardingStatus) return;

    try {
      const { data, error } = await supabase
        .from("onboarding_status")
        .update({
          completed: true,
          completed_at: new Date().toISOString(),
        })
        .eq("id", onboardingStatus.id ?? '')
        .select()
        .single();

      if (error) throw error;
      
      setOnboardingStatus(data as OnboardingStatus | null);
      setShowOnboarding(false);
      
      // Navigate to dashboard
      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      console.error("Error completing onboarding:", error);
    }
  };

  const restartOnboarding = async () => {
    if (!onboardingStatus) return;

    try {
      const { data, error } = await supabase
        .from("onboarding_status")
        .update({
          completed: false,
          step_welcome: false,
          step_players: false,
          step_data: false,
          completed_at: null,
        })
        .eq("id", onboardingStatus.id ?? '')
        .select()
        .single();

      if (error) throw error;
      
      setOnboardingStatus(data as OnboardingStatus | null);
      setShowOnboarding(true);
    } catch (error) {
      console.error("Error restarting onboarding:", error);
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        showOnboarding,
        onboardingStatus,
        loading,
        updateStep,
        completeOnboarding,
        restartOnboarding,
        checkOnboardingStatus,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
};
